import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { monitoramentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';

const toISO = (d: Date): string => d.toISOString().slice(0, 10);

const MonitoramentoInternacoes: React.FC = () => {
  const { municipioAtivo } = useAuth();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hoje = new Date();
  const tresMesesAtras = new Date();
  tresMesesAtras.setMonth(tresMesesAtras.getMonth() - 3);
  const [dataInicio, setDataInicio] = useState(toISO(tresMesesAtras));
  const [dataFim, setDataFim] = useState(toISO(hoje));

  const handleDateChange = (di: string, df: string) => {
    setDataInicio(di);
    setDataFim(df);
  };

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await monitoramentoService.getInternacoes(
          municipioAtivo.id,
          dataInicio,
          dataFim,
        );

        setData(result);
      } catch (err: any) {
        setError(
          err?.response?.data?.message || 'Erro ao carregar dados de internacoes.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [municipioAtivo?.id, dataInicio, dataFim]);

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', px: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Nenhum municipio selecionado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Selecione um municipio para visualizar dados de internacoes.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Skeleton variant="text" width="30%" height={48} />
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mt: 2, mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  const kpis = data?.kpis || {};
  const topCids = data?.topCids || [];
  const porClinica = data?.porClinica || [];
  const motivosAlta = data?.motivosAlta || [];

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Internacoes
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {municipioAtivo.nome}
          </Typography>
        </Box>
        <DateRangeSelector
          dataInicio={dataInicio}
          dataFim={dataFim}
          onChange={handleDateChange}
        />
      </Box>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <KpiCard
            titulo="Total de Internacoes"
            valor={(kpis.total_internacoes ?? 0).toLocaleString('pt-BR')}
            icone={<MeetingRoomIcon />}
            cor="#007AF0"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            titulo="Media de Permanencia"
            valor={`${(kpis.media_permanencia ?? 0).toLocaleString('pt-BR')} dias`}
            icone={<AccessTimeIcon />}
            cor="#E76F00"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard
            titulo="Com Diagnostico"
            valor={(kpis.com_diagnostico ?? 0).toLocaleString('pt-BR')}
            icone={<MedicalInformationIcon />}
            cor="#02786D"
          />
        </Grid>
      </Grid>

      {/* Top CIDs - BarChart */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Principais CIDs (Diagnosticos)
        </Typography>
        {topCids.length > 0 ? (
          <ResponsiveContainer width="100%" height={Math.max(topCids.length * 45, 250)}>
            <BarChart
              data={topCids}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickFormatter={(v: number) => v.toLocaleString('pt-BR')} />
              <YAxis
                type="category"
                dataKey="cid"
                width={80}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                labelFormatter={(label: any) => {
                  const item = topCids.find((c: any) => c.cid === label);
                  return item ? `${item.cid} - ${item.nome_cid}` : label;
                }}
                formatter={(value: any) => [
                  Number(value).toLocaleString('pt-BR'),
                  'Internacoes',
                ]}
              />
              <Bar dataKey="total" fill="#02786D" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de CIDs para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {/* Por Clinica - Tabela */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Internacoes por Clinica
        </Typography>
        {porClinica.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Clinica</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Media Permanencia (dias)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>% do Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {porClinica.map((row: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{row.nome_clinica || row.clinica}</TableCell>
                    <TableCell align="right">
                      {(row.total ?? 0).toLocaleString('pt-BR')}
                    </TableCell>
                    <TableCell align="right">
                      {(row.media_permanencia ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${(row.percentual ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de clinicas para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {/* Motivos de Alta - Tabela */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Motivos de Alta
        </Typography>
        {motivosAlta.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Motivo</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>% do Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {motivosAlta.map((row: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{row.motivo || row.descricao}</TableCell>
                    <TableCell align="right">
                      {(row.total ?? 0).toLocaleString('pt-BR')}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${(row.percentual ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de motivos de alta para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {municipioAtivo && data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="internacoes"
          contexto="dashboard"
          manualTrigger
        />
      )}

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify(data)}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoInternacoes;
